import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Typography } from 'antd';
import { addSeriesCharacters, getProject, getSeries, listGenerations } from '../illustration/api';
import { generationPortrait, isShelfPerson } from './shelfPicture';
import './SeriesPage.css';

const { Title, Paragraph } = Typography;

export default function AddToShelfPage() {
  const { seriesId, projectId } = useParams();
  const navigate = useNavigate();
  const [series, setSeries] = useState(null);
  const [project, setProject] = useState(null);
  const [generations, setGenerations] = useState([]);
  const [picked, setPicked] = useState([]);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancel = false;
    Promise.all([
      getSeries(seriesId),
      getProject(projectId),
      listGenerations(projectId).catch(() => ({ generations: [] })),
    ]).then(([shelf, book, pictures]) => {
      if (cancel) return;
      setSeries(shelf.series || null);
      setProject(book.project || null);
      setGenerations(pictures.generations || pictures || []);
    }).catch((err) => {
      if (!cancel) setError(err.message || 'Could not open this book');
    });
    return () => {
      cancel = true;
    };
  }, [seriesId, projectId]);

  const people = (project?.structure?.characters || []).filter(isShelfPerson);
  const onShelf = new Set(
    (series?.characters || []).map((card) => String(card.name || '').trim().toLowerCase())
  );

  function toggle(character) {
    const name = String(character.meta?.name || character.title || '').trim().toLowerCase();
    if (onShelf.has(name)) return;
    setPicked((current) => (
      current.includes(character.id)
        ? current.filter((id) => id !== character.id)
        : [...current, character.id]
    ));
  }

  async function putOnShelf() {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const result = await addSeriesCharacters(seriesId, projectId, picked);
      const skipped = (result.refused || []).filter((row) => row.reason === 'not-person-or-dog').length;
      if (skipped) setNotice('Vans, houses, and trees stay in the book.');
      navigate(`/series/${seriesId}`);
    } catch (err) {
      setError(err.message || 'Could not add those people');
      setSaving(false);
    }
  }

  return (
    <div className="workflow-shell__content">
      <Title level={1} style={{ fontFamily: 'var(--font-display)', marginTop: 0 }}>
        Put people on the shelf
      </Title>
      <Paragraph style={{ maxWidth: 'var(--measure-prose)', color: 'var(--color-text-secondary)' }}>
        {project
          ? `Choose who from ${project.title} stands on ${series?.title || 'this series'}. Each person is one place on the shelf.`
          : 'Choose who stands on the shelf.'}
      </Paragraph>
      {error ? <Alert type="error" showIcon title={error} style={{ marginBottom: 16 }} /> : null}
      {notice ? <Alert type="info" showIcon title={notice} style={{ marginBottom: 16 }} /> : null}
      <div className="series-shelf">
        {people.map((character) => {
          const name = character.meta?.name || character.title;
          const already = onShelf.has(String(name || '').trim().toLowerCase());
          const selected = picked.includes(character.id);
          const picture = generationPortrait(character, generations);
          return (
            <button
              key={character.id}
              type="button"
              className="series-person"
              aria-pressed={selected}
              disabled={already}
              onClick={() => toggle(character)}
            >
              {picture ? <img src={picture} alt="" /> : <span className="series-person__empty" aria-hidden="true" />}
              <span className="series-person__name">{name}</span>
              <span className="series-person__state">
                {already ? 'On the shelf' : selected ? 'Selected' : (character.meta?.kind === 'dog' ? 'Dog' : 'Person')}
              </span>
            </button>
          );
        })}
        {project && !people.length ? <Paragraph>This book has no people or dogs to put on the shelf.</Paragraph> : null}
      </div>
      <div className="series-shelf__edge" />
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Button type="primary" disabled={!picked.length} loading={saving} onClick={putOnShelf}>
          Put on the shelf
        </Button>
        <Button onClick={() => navigate(`/series/${seriesId}`)}>Back to the series</Button>
      </div>
    </div>
  );
}
